import React from 'react';
import type { ThemeStructure } from './theme-config';

interface BackgroundPatternProps {
  theme: ThemeStructure;
}

export function BackgroundPattern({ theme }: BackgroundPatternProps) {
  switch (theme.pattern) {
    case 'corner-dots':
      return (
        <>
          <div className="absolute top-0 left-0 h-4 w-4 rounded-full bg-white opacity-20" />
          <div className="absolute top-0 right-0 h-4 w-4 rounded-full bg-white opacity-20" />
          <div className="absolute bottom-0 left-0 h-4 w-4 rounded-full bg-white opacity-20" />
          <div className="absolute right-0 bottom-0 h-4 w-4 rounded-full bg-white opacity-20" />
        </>
      );

    case 'horizontal-lines':
      return (
        <>
          <div className="absolute top-0 left-0 h-px w-full bg-white opacity-20" />
          <div className="absolute bottom-0 left-0 h-px w-full bg-white opacity-20" />
        </>
      );

    case 'circle-bg':
      return (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-32 w-32 rounded-full bg-white opacity-10" />
        </div>
      );

    case 'slashes':
      return (
        <div className="absolute inset-0 overflow-hidden">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="absolute h-px w-20 rotate-45 bg-white opacity-20"
              style={{
                top: `${10 + i * 20}%`,
                left: `${-20 + i * 30}%`,
              }}
            />
          ))}
        </div>
      );

    case 'stacked':
      return (
        <div className="absolute flex h-full w-full flex-col justify-between">
          <div className="h-1 w-full bg-white opacity-20" />
          <div className="h-1 w-full bg-white opacity-20" />
        </div>
      );

    case 'side-stripe':
      return (
        <div className="absolute top-0 left-0 h-full w-2 bg-white opacity-20" />
      );

    case 'bordered':
      return (
        <div className="absolute inset-2 rounded-lg border-2 border-white opacity-20" />
      );

    case 'grid':
      return (
        <div className="absolute inset-0 grid grid-cols-3 grid-rows-3">
          {Array.from({ length: 9 }).map((_, i) => (
            <div key={i} className="border border-white opacity-5" />
          ))}
        </div>
      );

    case 'diagonal':
      return (
        <div className="absolute inset-0 overflow-hidden">
          <div className="transform-origin-center absolute top-0 left-0 h-full w-full scale-150 -rotate-12 bg-white opacity-10" />
        </div>
      );

    // New patterns
    case 'mesh-grid':
      return (
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 grid grid-cols-4 grid-rows-4">
            {Array.from({ length: 16 }).map((_, i) => (
              <div
                key={i}
                className="flex items-center justify-center border border-white/10"
              >
                {i % 3 === 0 && (
                  <div className="h-1.5 w-1.5 rounded-full bg-white/20" />
                )}
              </div>
            ))}
          </div>
        </div>
      );

    case 'dots-matrix':
      return (
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 grid grid-cols-6 grid-rows-6">
            {Array.from({ length: 36 }).map((_, i) => (
              <div key={i} className="flex items-center justify-center">
                {i % 2 === 0 && (
                  <div className="h-1 w-1 rounded-full bg-white/20" />
                )}
              </div>
            ))}
          </div>
        </div>
      );

    case 'wave-lines':
      return (
        <div className="absolute inset-0 flex flex-col items-center justify-between overflow-hidden">
          {Array.from({ length: 3 }).map((_, i) => (
            <div
              key={i}
              className="h-1 w-full bg-white/15"
              style={{
                transform: `translateY(${i * 5}px) rotate(${(i % 2 === 0 ? 1 : -1) * 1}deg)`,
              }}
            />
          ))}
        </div>
      );

    case 'geometric':
      return (
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-10 -left-10 h-40 w-40 rotate-45 bg-white/10" />
          <div className="absolute -right-20 -bottom-20 h-40 w-40 rounded-full bg-white/5" />
        </div>
      );

    case 'circuit-board':
      return (
        <div className="absolute inset-0 overflow-hidden">
          {Array.from({ length: 8 }).map((_, i) => (
            <React.Fragment key={i}>
              <div
                className="absolute h-px bg-white/15"
                style={{
                  width: `${10 + Math.random() * 20}%`,
                  top: `${10 + i * 10}%`,
                  left: `${Math.random() * 60}%`,
                }}
              />
              <div
                className="absolute w-px bg-white/15"
                style={{
                  height: `${10 + Math.random() * 20}%`,
                  left: `${10 + i * 10}%`,
                  top: `${Math.random() * 60}%`,
                }}
              />
            </React.Fragment>
          ))}
        </div>
      );

    case '3d-cube':
      return (
        <div className="absolute inset-0 flex items-center justify-center overflow-hidden">
          <div className="relative h-24 w-24">
            <div className="absolute inset-0 rotate-12 transform border-2 border-white/20" />
            <div className="absolute inset-0 -rotate-12 transform border-2 border-white/15" />
          </div>
        </div>
      );

    case 'topography':
      return (
        <div className="absolute inset-0 overflow-hidden">
          {Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-white/5"
              style={{
                width: `${30 + Math.random() * 40}%`,
                height: `${30 + Math.random() * 40}%`,
                top: `${Math.random() * 80}%`,
                left: `${Math.random() * 80}%`,
                opacity: 0.05 + i * 0.02,
              }}
            />
          ))}
        </div>
      );

    case 'sunset':
      return (
        <div className="absolute inset-0 overflow-hidden">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="absolute left-0 w-full bg-white/10"
              style={{
                height: '3px',
                bottom: `${15 + i * 15}%`,
                opacity: 0.2 - i * 0.04,
              }}
            />
          ))}
        </div>
      );

    case 'night-sky':
      return (
        <div className="absolute inset-0 overflow-hidden">
          {Array.from({ length: 20 }).map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full bg-white"
              style={{
                width: i % 5 === 0 ? '2px' : '1px',
                height: i % 5 === 0 ? '2px' : '1px',
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                opacity: 0.2 + Math.random() * 0.6,
              }}
            />
          ))}
        </div>
      );

    case 'leafy':
      return (
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-5 -right-5 h-20 w-20 rotate-45 opacity-10">
            <div className="absolute inset-0 rounded-br-3xl border-r-2 border-b-2 border-white" />
          </div>
          <div className="absolute -bottom-5 -left-5 h-16 w-16 -rotate-45 opacity-10">
            <div className="absolute inset-0 rounded-tl-3xl border-t-2 border-l-2 border-white" />
          </div>
        </div>
      );

    default:
      return null; // Minimal pattern has no background decoration
  }
}
